"use client";

import { Show, SignInButton, UserButton } from "@clerk/nextjs";

import { motion } from "framer-motion";
import {
  Bot,
  Boxes,
  CheckCircle2,
  ClipboardCheck,
  Cpu,
  Database,
  FileSpreadsheet,
  Network,
  RotateCcw,
  ServerCog,
  Shield,
  Sparkles,
  Workflow,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useTheme } from "next-themes";
import * as React from "react";
import { Logo } from "@/components/Logo";
import { MainMenu } from "@/components/MainMenu";
import { ModeToggle } from "@/components/ModeToggle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import workflowImage from "@/public/images/workflow.jpg";

const CONTACT_URL = "https://calendar.app.google/4E1JwtdJMcbrdG1D8";

export default function LlamoxPage() {
  const { theme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const isLight = mounted && theme === "light";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 w-full z-50 bg-background/95 dark:bg-background/80 backdrop-blur-xl border-b border-border shadow-sm">
        <nav className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo />
            <div className="hidden md:block">
              <MainMenu />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="md:hidden">
              <MainMenu />
            </div>
            <Show when="signed-out">
              <SignInButton mode="modal">
                <Button variant="outline" className="shadow-sm">
                  Sign In
                </Button>
              </SignInButton>
            </Show>
            <Show when="signed-in">
              <UserButton />
              <Link href="/console">
                <Button variant="default" className="shadow-sm">
                  Launch App
                </Button>
              </Link>
            </Show>
            <ModeToggle />
          </div>
        </nav>
      </header>

      <main className="flex-grow">
        {/* Hero Section with Background */}
        <section className="relative w-full overflow-hidden min-h-[85vh] flex items-center justify-center">
          {/* Dynamic Background Image */}
          <div className="absolute inset-0 z-0">
            <Image
              src={workflowImage}
              alt="Governed AI operations visualization"
              className="object-cover object-center w-full h-full opacity-40 dark:opacity-30"
              fill
              priority
              sizes="100vw"
              quality={90}
            />
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-violet-900/20 via-purple-900/40 to-background/95" />
          </div>

          {/* Background Pattern */}
          <div className="absolute inset-0 bg-grid-white/10 [mask-image:radial-gradient(white,transparent_80%)] pointer-events-none z-10 opacity-30" />

          {/* Floating Animated Icons */}
          <motion.div
            className="absolute top-20 left-8 w-16 h-16 bg-violet-500/10 rounded-xl hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-violet-500/20 z-10"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Bot className="w-8 h-8 text-violet-500/70" />
          </motion.div>
          <motion.div
            className="absolute top-32 right-12 w-14 h-14 bg-purple-500/10 rounded-full hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-purple-500/20 z-10"
            style={{ animationDelay: "1s" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Workflow className="w-7 h-7 text-purple-500/70" />
          </motion.div>
          <motion.div
            className="absolute bottom-32 left-16 w-14 h-14 bg-indigo-500/10 rounded-xl hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-indigo-500/20 z-10"
            style={{ animationDelay: "2s" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Shield className="w-7 h-7 text-indigo-500/70" />
          </motion.div>
          <motion.div
            className="absolute bottom-20 right-20 w-14 h-14 bg-blue-500/10 rounded-full hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-blue-500/20 z-10"
            style={{ animationDelay: "0.5s" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Sparkles className="w-7 h-7 text-blue-500/70" />
          </motion.div>

          {/* Hero Content */}
          <div className="relative z-20 section-container">
            <div className="flex flex-col items-center text-center max-w-5xl mx-auto px-4 py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span
                  className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-medium mb-6 backdrop-blur-sm"
                  style={{
                    backgroundColor: isLight
                      ? "rgba(17, 24, 39, 0.2)"
                      : "rgba(255, 255, 255, 0.2)",
                    color: isLight ? "#111827" : "#ffffff",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    borderColor: isLight
                      ? "rgba(17, 24, 39, 0.3)"
                      : "rgba(255, 255, 255, 0.3)",
                  }}
                >
                  <Cpu
                    className="w-4 h-4 mr-2"
                    style={{ color: isLight ? "#111827" : "#ffffff" }}
                  />
                  The governed AI operations platform
                </span>
                <h1
                  className="mb-6"
                  style={{ color: isLight ? "#111827" : "#ffffff" }}
                >
                  Describe it. Approve it. It's live.
                </h1>
                <p
                  className="text-xl mb-6 leading-relaxed"
                  style={{
                    color: isLight ? "#374151" : "rgba(255, 255, 255, 0.9)",
                  }}
                >
                  Llamox turns a plain-language request into a working app,
                  workflow, spreadsheet, stream, or data pipeline. Agents build
                  the preview. You review, modify, and approve. The platform
                  deploys approved changes, monitors execution, records
                  decisions, and keeps rollback paths where the runtime supports
                  them.
                </p>
                <p
                  className="text-sm mb-10"
                  style={{
                    color: isLight ? "#4b5563" : "rgba(255, 255, 255, 0.7)",
                  }}
                >
                  Llamox is the control plane underneath Kautious — and
                  available directly to teams that want to build on it.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Button
                    size="lg"
                    className="hover:opacity-90"
                    style={{
                      backgroundColor: isLight ? "#7c3aed" : "#8b5cf6",
                      color: "#ffffff",
                    }}
                    asChild
                  >
                    <a href={CONTACT_URL} target="_blank" rel="noreferrer">
                      Request early access
                    </a>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="hover:opacity-80"
                    style={{
                      borderColor: isLight ? "#111827" : "#ffffff",
                      color: isLight ? "#111827" : "#ffffff",
                      backgroundColor: "transparent",
                    }}
                    asChild
                  >
                    <Link href="#capabilities">See the platform</Link>
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Above the fold callouts */}
        <section className="section-padding bg-secondary/5">
          <div className="section-container">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <Card className="p-8 card-hover border-l-4 border-l-violet-500">
                <div className="w-12 h-12 bg-violet-500/10 rounded-xl flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-violet-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  Generate the thing
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Describe the app, workflow, spreadsheet, or stream you need.
                  Llamox produces a runnable preview — not another static
                  suggestion. Don't like it? Tell it. It rebuilds while you
                  watch.
                </p>
              </Card>
              <Card className="p-8 card-hover border-l-4 border-l-purple-500">
                <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mb-4">
                  <ClipboardCheck className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">You hold the pen</h3>
                <p className="text-muted-foreground leading-relaxed">
                  High-impact actions stop for human review, policy checks, and
                  workspace-scoped permissions before reaching production. Your
                  team decides what requires approval before it ships.
                </p>
              </Card>
              <Card className="p-8 card-hover border-l-4 border-l-indigo-500">
                <div className="w-12 h-12 bg-indigo-500/10 rounded-xl flex items-center justify-center mb-4">
                  <RotateCcw className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  Deploy with rollback
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Promote generated apps and supported automations through
                  staged deployment, health checks, and version history.
                  Rollback is part of the operating model.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* The Problem */}
        <section className="section-padding">
          <div className="section-container max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Stop choosing between the queue and the cowboy demo.
            </h2>
            <p className="text-lg text-muted-foreground mb-4">
              Option one is the queue: a smart person waiting six months for a
              three-table CRUD app that was "prioritized for next quarter." That
              is not an engineering problem — it is a permission problem.
            </p>
            <p className="text-lg text-muted-foreground mb-4">
              Option two is the demo: a chatbot wired to a few tools that can't
              be trusted with real work. No durable state, no approval model, no
              audit trail, no safe sandbox, no rollback. That is agent theater.
            </p>
            <p className="text-lg text-foreground">
              Llamox is the third option. Describe the tool. The AI builds a
              preview. You approve what should move forward. The audit trail is
              written as the work moves.
            </p>
          </div>
        </section>

        {/* What Llamox Does */}
        <section id="capabilities" className="section-padding bg-muted/30">
          <div className="section-container">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                One platform for AI-generated operations
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                The output is not just a response. It is a governed artifact
                your team can inspect, modify, approve, deploy, observe, and
                improve.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                {
                  icon: Boxes,
                  title: "AI App Builder",
                  body: "Turn a prompt, spreadsheet, or workflow idea into a working application — preview, schema, UI, runtime, and data operations — then route deployment through approval, versioning, and rollback.",
                },
                {
                  icon: Bot,
                  title: "Managed Agents",
                  body: "Run agents that use real tools without unlimited freedom. Hosted agent loops connect to workspace-scoped sandboxes, memory, web search, workflow triggers, and governed outbound access.",
                },
                {
                  icon: Workflow,
                  title: "Workflow Orchestration",
                  body: "Build long-running workflows with explicit approval checkpoints. Durable orchestration lets teams pause for human review without burning compute or losing state.",
                },
                {
                  icon: FileSpreadsheet,
                  title: "Spreadsheet Automation",
                  body: "Generate, update, import, and version Google Sheets and Excel workbooks. Convert messy CSVs into structured outputs with review, conflict handling, and rollback.",
                },
                {
                  icon: Database,
                  title: "Data Pipelines & Streams",
                  body: "Create ETL, ELT, and real-time processing flows from natural language, with file analysis, transformations, lineage, stream generation, health checks, and metrics.",
                },
                {
                  icon: ServerCog,
                  title: "Secure Sandbox Execution",
                  body: "Run generated code in isolated execution providers — fast edge execution, Python data analysis, full-language support, or interactive coding sessions.",
                },
                {
                  icon: Shield,
                  title: "Enterprise Governance",
                  body: "PolicyGate, OutputGuard, WriteGuard, audit logs, incident workflows, workspace isolation, and human approvals keep agents inside the boundaries your team defines.",
                },
                {
                  icon: Network,
                  title: "Production Runtime",
                  body: "Deploy generated apps and supported automations on Cloudflare's edge with Workers, Durable Objects, Workflows, R2, D1, KV, Convex, and Workers for Platforms.",
                },
                {
                  icon: ClipboardCheck,
                  title: "Tool-Connected Agents",
                  body: "Connect agents to GitHub, Slack, Gmail, Notion, Salesforce, Jira, Linear, and more through 500+ OAuth-backed integrations — with policy checks and audit logs.",
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <Card key={item.title} className="p-8 card-hover">
                    <div className="w-12 h-12 bg-violet-500/10 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-violet-600" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {item.body}
                    </p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* The Llamox Difference */}
        <section className="section-padding">
          <div className="section-container max-w-5xl mx-auto">
            <h2 className="text-3xl font-semibold text-center mb-12">
              The Llamox Difference
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title:
                    "Most AI tools stop at generation. Llamox owns the lifecycle.",
                  body: "Generate the artifact. Review the diff. Approve the action. Deploy the runtime. Monitor the execution. Roll back versioned deployments when needed.",
                },
                {
                  title:
                    "Most agent platforms assume trust. Llamox assumes blast radius.",
                  body: "Agents can be useful and still need limits. Every workspace gets its own policies, secrets, tools, storage, approvals, and audit trails.",
                },
                {
                  title:
                    "Most stacks force a choice between the queue and the demo.",
                  body: "When approval, identity, state, version history, and rollback are built in where they matter, responsible teams can let AI do more — this week, not next quarter.",
                },
                {
                  title: "The AI does the work. You hold the pen.",
                  body: "Production changes can require a human saying ship. You see the preview, demand changes in plain English, and approve before it touches production.",
                },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4">
                  <CheckCircle2 className="w-6 h-6 text-violet-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">{item.title}</h3>
                    <p className="text-muted-foreground">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technical Credibility */}
        <section className="section-padding bg-muted/30">
          <div className="section-container max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-semibold mb-4">
              Built on a production-grade edge architecture
            </h2>
            <p className="text-muted-foreground mb-8">
              The pieces that make prototypes production-ready are part of the
              platform.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Cloudflare Workers",
                "Durable Objects",
                "Cloudflare Workflows",
                "Workers for Platforms",
                "Convex",
                "R2 · D1 · KV",
                "Composio (500+ integrations)",
                "Multi-provider sandboxes",
              ].map((tech) => (
                <span
                  key={tech}
                  className="text-sm text-foreground/80 border border-border bg-background rounded-full px-4 py-2"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="section-padding bg-gradient-to-br from-violet-600 to-purple-600 text-white">
          <div className="section-container text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-4xl font-semibold mb-6 text-white">
                Give AI the work. Keep the controls.
              </h2>
              <p className="text-xl mb-8 text-white/90">
                Walk in Monday with the preview. Bring the audit trail on
                Tuesday. We onboard in cohorts — tell us what you'd build first.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  size="lg"
                  variant="secondary"
                  className="bg-white text-violet-600 hover:bg-white/90"
                  asChild
                >
                  <a href={CONTACT_URL} target="_blank" rel="noreferrer">
                    Request early access
                  </a>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  asChild
                >
                  <Link href="/kautious">See it powering Kautious</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t bg-background/50 backdrop-blur-sm">
        <div className="max-w-screen-xl mx-auto px-6 py-12">
          <div className="flex justify-between items-center">
            <Logo />
            <div className="flex items-center gap-6">
              <Link
                href="/terms"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Terms
              </Link>
              <Link
                href="/privacy"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Privacy
              </Link>
              <ModeToggle />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
